import { UppyFile } from '@uppy/core';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom-v5-compat';
import { isRequiredFeatureEnabled } from 'src/components/auth';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import {
  FileUpload,
  FileUploadProvider,
  useFileUploadContext,
} from 'src/components/molecules/FileUpload';
import { UploadedEvidence } from 'src/components/molecules/UploadedEvidence';
import { Loading } from 'src/components/pages/Loading';

import { Link } from 'src/components/atoms';
import { useUploadReportDocumentation } from 'src/components/molecules/FileUpload/hooks/useUploadReportDocumentation';
import {
  UploadSource,
  useGetOrganisationFeaturesQuery,
  useGetReportByIdQuery,
} from 'src/graphql';
import { getCurrentUser } from 'src/reducers/Reporting.slice';
import {
  Feature,
  FeatureFlag,
  Platform,
  SubmissionType,
  UploadScanStatus,
} from 'src/types';
import { addingFilesAsEvidenceIntercomArticle } from 'src/utils/intercomArticles';
import { default as I18next, default as i18next } from '../../../../i18n';
import { ReportCard } from '../../Common/ReportCard/ReportCard';
import { ReportContainer, StyledLink } from '../ReportingLayout.styles';
import {
  AdditionalEvidenceHeader,
  DescriptionContainer,
  DescriptionHeader,
  DescriptionText,
  FileUploadContainer,
} from './ReportingAddEvidence.styles';

const InnerReportingAddEvidence = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { reportId } = useParams<{ reportId: string }>();

  const { handleFileRemove } = useFileUploadContext();

  const features = useGetOrganisationFeaturesQuery();
  const isFileDownloadDisabled = isRequiredFeatureEnabled(
    (features.data?.getOrganisationFeatures as Feature[]) || [],
    FeatureFlag.REPORTER_VIEW_EVIDENCE_DISABLED,
  );

  const { data: reportData, isLoading } = useGetReportByIdQuery(
    {
      reportId: reportId!,
    },
    {
      enabled: !!reportId,
    },
  );
  const report = reportData?.getReportById;

  const files = report?.postSubmissionFiles || [];
  const existingUploadedFiles =
    files.map((file) => ({
      size: file.size,
      dateAdded: file.createdAt,
      fileExtension: file.fileExtension,
      fileS3Path: file.s3path,
      status: file.scanStatus as UploadScanStatus,
    })) || [];
  const isReportClosed = report?.status === 'CLOSED';
  const showUploadedFiles =
    Boolean(existingUploadedFiles.length) && Boolean(reportData);

  const textDirection = I18next.dir ? I18next.dir() : 'ltr';

  const { uploadFilesToReportPostSubmission } = useUploadReportDocumentation({
    uploadSource: UploadSource.ReporterHubPostsubmission,
    onPostSubmissionUploadSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['GetReportById', { reportId }],
      });
    },
  });

  const onConfirm = async (files: UppyFile[]) => {
    const { successfulFileUploads } = await uploadFilesToReportPostSubmission({
      files: files,
      reportId: reportId ?? '',
    });

    successfulFileUploads.forEach((file) => {
      handleFileRemove(file.id);
    });
  };

  const linkComponentToBeEmbedded = (
    <Link
      href={addingFilesAsEvidenceIntercomArticle}
      size="small"
      target="_blank"
      rel="no-referrer"
      data-testid="file-upload-help-article-link"
    />
  );

  if (isLoading || !report) {
    return <Loading />;
  }

  return (
    <ReportContainer>
      <StyledLink
        to={`/reporting/reports/${reportId}`}
        data-testid="report-back-button"
      >
        <StyledIcon
          className={`uil uil-angle-${
            textDirection === 'ltr' ? 'left' : 'right'
          }`}
          size="28px"
        />
        {t('public.PublicMoreEvidence.backToReportId', {
          reportId: report.reportReadableId,
        })}
      </StyledLink>
      <ReportCard report={report} version={'report-view'} />

      {!isReportClosed && (
        <>
          <DescriptionContainer>
            <DescriptionHeader>
              {t('reporting.ReportingAddEvidence.descriptionHeader')}
            </DescriptionHeader>
            <DescriptionText>
              <Trans
                i18n={i18next}
                i18nKey="public.PublicMoreEvidence.description"
                components={{
                  a: linkComponentToBeEmbedded,
                }}
              />
            </DescriptionText>
          </DescriptionContainer>

          <FileUploadContainer>
            <FileUpload
              dragDropText={t('public.FileUpload.dragDrop')}
              highlightedText={t('public.FileUpload.highlighted')}
              fileLimitText={t('public.FileUpload.fileLimit')}
              informationText={t('public.FileUpload.information')}
              addFilesButtonText={t('public.FileUpload.addFiles')}
              tooltipFileOptions={[
                {
                  type: t('public.FileUpload.tooltipImage'),
                  options: t('public.FileUpload.tooltipImageOptions'),
                },
                {
                  type: t('public.FileUpload.tooltipText'),
                  options: t('public.FileUpload.tooltipTextOptions'),
                },
                {
                  type: t('public.FileUpload.tooltipVideo'),
                  options: t('public.FileUpload.tooltipVideoOptions'),
                },
                {
                  type: t('public.FileUpload.tooltipAudio'),
                  options: t('public.FileUpload.tooltipAudioOptions'),
                },
                {
                  type: t('public.FileUpload.tooltipDocuments'),
                  options: t('public.FileUpload.tooltipDocumentsOptions'),
                },
              ]}
              confirmButtonText={t('public.FileUpload.confirm')}
              uploadFilesButtonText={t('public.FileUpload.uploadFiles')}
              onConfirm={onConfirm}
            />
          </FileUploadContainer>
        </>
      )}

      <div>
        <AdditionalEvidenceHeader>
          {t('public.PublicMoreEvidence.additionalEvidenceHeader')}
        </AdditionalEvidenceHeader>
        {showUploadedFiles ? (
          <UploadedEvidence
            files={existingUploadedFiles}
            reportId={reportId ?? ''}
            hideTitle
            localisedText={{
              tableTitle: t('public.Evidence.tableTitle'),
              webErrorMessage: t('public.Evidence.webErrorMessage'),
              mobileErrorMessage: t('public.Evidence.mobileErrorMessage'),
              nameHeading: t('public.Evidence.nameHeading'),
              addedHeading: t('public.Evidence.addedHeading'),
              statusHeading: t('public.Evidence.statusHeading'),
              failedToOpenFileMessage: t(
                'public.Evidence.failedToOpenFileMessage',
              ),
              fileStatuses: {
                pendingUpload: t('public.Evidence.fileStatuses.pendingUpload'),
                passedUpload: t('public.Evidence.fileStatuses.passedUpload'),
                infectedUpload: t(
                  'public.Evidence.fileStatuses.infectedUpload',
                ),
                readyForDownload: t(
                  'public.Evidence.fileStatuses.readyForDownload',
                ),
              },
            }}
            baseFileName="AdditionalEvidence"
            downloadEvidenceDisabled={isFileDownloadDisabled}
          />
        ) : (
          <DescriptionText>
            {t('reporting.ReportingAddEvidence.emptyMessage')}
          </DescriptionText>
        )}
      </div>
    </ReportContainer>
  );
};

export const ReportingAddEvidence = () => {
  const { data: featuresData } = useGetOrganisationFeaturesQuery();
  const currentUser = useSelector(getCurrentUser);
  const organisationId = currentUser.organisation?.id || '';

  const isSegmentEnabled = isRequiredFeatureEnabled(
    (featuresData?.getOrganisationFeatures as Feature[]) || [],
    FeatureFlag.FRONTEND_SEGMENT_TRACKING,
  );

  return (
    <FileUploadProvider
      id="reporting-upload"
      organisationId={organisationId}
      segmentEnabled={isSegmentEnabled}
      submissionType={SubmissionType.POST_SUBMISSION}
      platform={Platform.REPORTING}
    >
      <InnerReportingAddEvidence />
    </FileUploadProvider>
  );
};
